import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatabaseBackup, Download, Upload, AlertTriangle, Mail } from "lucide-react";

const FREQS = [
  ["30min", "Every 30 minutes"],
  ["hourly", "Hourly"],
  ["daily", "Daily (3:00 AM IST)"],
  ["weekly", "Weekly (Sunday 3:00 AM IST)"],
];

const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(2)} MB` : `${(n / 1024).toFixed(1)} KB`);
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

export default function AdminBackup() {
  const [backups, setBackups] = useState([]);
  const [settings, setSettings] = useState({ backup_email: "", backup_frequency: "30min" });
  const [saving, setSaving] = useState(false);
  const [backing, setBacking] = useState(false);
  const [file, setFile] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [restoring, setRestoring] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/backups").then(r => setBackups(r.data)).catch(e => toast.error(e.response?.data?.detail || "Load failed"));
    api.get("/admin/backup/settings").then(r => setSettings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/admin/backup/settings", settings);
      toast.success("Backup settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const backupNow = async () => {
    setBacking(true);
    try {
      const { data } = await api.post("/admin/backups");
      toast.success(`Backup created — ${fmtSize(data.size)}, ${data.record_count} records` +
        (data.email_sent_to ? ` · emailed to ${data.email_sent_to}` : ""));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Backup failed"); }
    finally { setBacking(false); }
  };

  const download = async (b) => {
    try {
      const r = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = b.filename; a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const restore = async () => {
    if (!file || confirmText !== "RESTORE") return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirm", confirmText);
      const { data } = await api.post("/admin/backups/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Restore complete — ${Object.keys(data.restored).length} collections replaced`);
      setFile(null); setConfirmText("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
    finally { setRestoring(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="backup-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><DatabaseBackup className="w-7 h-7"/> Backup &amp; Restore</h1>
          <p className="text-muted-foreground">Platform-level master &amp; configuration data only — tenant transactions are never included.</p>
        </div>
        <Button onClick={backupNow} disabled={backing} data-testid="backup-now-btn">
          <DatabaseBackup className="w-4 h-4 mr-1"/> {backing ? "Creating backup…" : "Backup Now"}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Backup Settings</CardTitle>
          <CardDescription>Backup Now also emails the file to this address. Automatic backups follow the chosen frequency.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-end gap-4 flex-wrap">
          <div>
            <Label>Admin Email</Label>
            <div className="relative mt-1">
              <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"/>
              <Input data-testid="backup-email-input" type="email" value={settings.backup_email}
                onChange={(e) => setSettings(s => ({ ...s, backup_email: e.target.value }))}
                placeholder="admin@yourcompany.com" className="pl-8 w-72"/>
            </div>
          </div>
          <div>
            <Label>Automatic Backup Frequency</Label>
            <Select value={settings.backup_frequency} onValueChange={(v) => setSettings(s => ({ ...s, backup_frequency: v }))}>
              <SelectTrigger data-testid="backup-frequency-select" className="mt-1 w-64"><SelectValue/></SelectTrigger>
              <SelectContent>
                {FREQS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`backup-freq-${v}`}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={saveSettings} disabled={saving} data-testid="backup-settings-save" variant="outline">
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Backups</CardTitle>
          <CardDescription>Every backup file created manually or by schedule. Download keeps your own durable copy.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="backup-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Records</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Emailed To</TableHead>
                <TableHead className="text-right">Download</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {backups.map(b => (
                <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                  <TableCell className="font-medium">{fmtDate(b.created_at)}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtTime(b.created_at)}</TableCell>
                  <TableCell className="font-mono text-xs" data-testid={`backup-size-${b.id}`}>{fmtSize(b.size)}</TableCell>
                  <TableCell className="font-mono text-xs">{b.record_count}</TableCell>
                  <TableCell><Badge variant={b.trigger === "manual" ? "default" : "secondary"} className="text-[10px] uppercase">{b.trigger}</Badge></TableCell>
                  <TableCell className="text-xs">{b.email_sent_to || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => download(b)} data-testid={`backup-download-${b.id}`}>
                      <Download className="w-4 h-4"/>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!backups.length && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8" data-testid="backup-empty">
                  No backups yet — click Backup Now to create the first one
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-destructive/40" data-testid="restore-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5"/> Restore from Backup</CardTitle>
          <CardDescription>Upload a previously downloaded platform backup file.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3 border border-destructive/40 bg-destructive/10 rounded-sm p-3" data-testid="restore-warning">
            <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0"/>
            <div className="text-sm">
              <strong className="text-destructive">Warning:</strong> restoring <strong>overwrites all existing platform-level data</strong>
              (organisations, establishments, plans, roles &amp; permissions, shared masters, and user accounts) with the contents of the backup file.
              This cannot be undone. Download a fresh backup first if unsure.
            </div>
          </div>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <Label>Backup File (.json)</Label>
              <Input ref={fileRef} data-testid="restore-file-input" type="file" accept=".json,application/json"
                onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 w-72"/>
            </div>
            <div>
              <Label>Type RESTORE to confirm</Label>
              <Input data-testid="restore-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                placeholder="RESTORE" className="mt-1 w-48 font-mono"/>
            </div>
            <Button onClick={restore} disabled={restoring || !file || confirmText !== "RESTORE"}
              data-testid="restore-btn" variant="destructive">
              {restoring ? "Restoring…" : "Restore Now"}
            </Button>
          </div>
          {file && <div className="text-xs text-muted-foreground" data-testid="restore-file-name">Selected: {file.name} ({fmtSize(file.size)})</div>}
        </CardContent>
      </Card>
    </div>
  );
}
